/**
 * A minimal ZIP writer (PKWARE APPNOTE 6.3): deflated entries when the platform has
 * `CompressionStream("deflate-raw")` (current browsers and Bun), stored entries otherwise.
 * No ZIP64, so the archive and every entry must stay below 4 GiB, far above the upload limits.
 *
 * Browser-safe and dependency-free.
 */

export interface ZipEntry {
  /** Path inside the archive, forward slashes, e.g. "eeg.csv" or "meta/session.json". */
  name: string;
  data: Uint8Array | string;
  /** Modification time, epoch ms (default: when the archive is written). */
  modified?: number;
}

export interface ZipOptions {
  /** Deflate entries when the platform supports it (default true). */
  compress?: boolean;
}

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

export function crc32(data: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < data.length; i++) c = CRC_TABLE[(c ^ data[i]!) & 0xff]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/** Local time in MS-DOS format, as ZIP readers display it. Years before 1980 clamp to 1980. */
function dosTime(ms: number): { time: number; date: number } {
  const d = new Date(ms);
  const year = Math.max(1980, d.getFullYear());
  return {
    time: (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1),
    date: ((year - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate(),
  };
}

const canDeflate = () => typeof CompressionStream === "function";

async function deflateRaw(data: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new CompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** Builds a ZIP archive from the entries, in order. Duplicate or unsafe names are refused. */
export async function createZip(entries: ZipEntry[], opts: ZipOptions = {}): Promise<Uint8Array> {
  const enc = new TextEncoder();
  const seen = new Set<string>();
  const now = Date.now();
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;

  for (const entry of entries) {
    if (!entry.name || entry.name.startsWith("/") || entry.name.includes("\\") || entry.name.split("/").includes("..")) {
      throw new Error(`createZip: unsafe entry name "${entry.name}"`);
    }
    if (seen.has(entry.name)) throw new Error(`createZip: duplicate entry "${entry.name}"`);
    seen.add(entry.name);
    const name = enc.encode(entry.name);
    const raw = typeof entry.data === "string" ? enc.encode(entry.data) : entry.data;
    const crc = crc32(raw);
    let method = 0;
    let body = raw;
    if (opts.compress !== false && canDeflate() && raw.length > 0) {
      const packed = await deflateRaw(raw);
      if (packed.length < raw.length) { method = 8; body = packed; }
    }
    const { time, date } = dosTime(entry.modified ?? now);
    const flags = 0x0800; // names are UTF-8

    const local = new Uint8Array(30 + name.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true);
    lv.setUint16(4, 20, true);
    lv.setUint16(6, flags, true);
    lv.setUint16(8, method, true);
    lv.setUint16(10, time, true);
    lv.setUint16(12, date, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, body.length, true);
    lv.setUint32(22, raw.length, true);
    lv.setUint16(26, name.length, true);
    lv.setUint16(28, 0, true);
    local.set(name, 30);

    const central = new Uint8Array(46 + name.length);
    const cv = new DataView(central.buffer);
    cv.setUint32(0, 0x02014b50, true);
    cv.setUint16(4, 20, true);
    cv.setUint16(6, 20, true);
    cv.setUint16(8, flags, true);
    cv.setUint16(10, method, true);
    cv.setUint16(12, time, true);
    cv.setUint16(14, date, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, body.length, true);
    cv.setUint32(24, raw.length, true);
    cv.setUint16(28, name.length, true);
    cv.setUint32(42, offset, true);
    central.set(name, 46);

    locals.push(local, body);
    centrals.push(central);
    offset += local.length + body.length;
    if (offset > 0xffffffff) throw new Error("createZip: archive exceeds 4 GiB (ZIP64 is not supported)");
  }

  const centralSize = centrals.reduce((n, c) => n + c.length, 0);
  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(8, entries.length, true);
  ev.setUint16(10, entries.length, true);
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, offset, true);

  const out = new Uint8Array(offset + centralSize + end.length);
  let at = 0;
  for (const part of [...locals, ...centrals, end]) { out.set(part, at); at += part.length; }
  return out;
}

/** Reads back an archive written by `createZip` (used by tests and to verify a ZIP before upload). */
export async function readZip(zip: Uint8Array): Promise<{ name: string; data: Uint8Array }[]> {
  const v = new DataView(zip.buffer, zip.byteOffset, zip.byteLength);
  let eocd = -1;
  for (let i = zip.length - 22; i >= Math.max(0, zip.length - 22 - 65535); i--) {
    if (v.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  }
  if (eocd < 0) throw new Error("readZip: no end of central directory");
  const count = v.getUint16(eocd + 10, true);
  let at = v.getUint32(eocd + 16, true);
  const dec = new TextDecoder();
  const out: { name: string; data: Uint8Array }[] = [];
  for (let i = 0; i < count; i++) {
    if (v.getUint32(at, true) !== 0x02014b50) throw new Error("readZip: bad central directory entry");
    const method = v.getUint16(at + 10, true);
    const crc = v.getUint32(at + 16, true);
    const size = v.getUint32(at + 20, true);
    const nameLen = v.getUint16(at + 28, true), extraLen = v.getUint16(at + 30, true), commentLen = v.getUint16(at + 32, true);
    const localAt = v.getUint32(at + 42, true);
    const name = dec.decode(zip.subarray(at + 46, at + 46 + nameLen));
    const dataAt = localAt + 30 + v.getUint16(localAt + 26, true) + v.getUint16(localAt + 28, true);
    const body = zip.subarray(dataAt, dataAt + size);
    let data: Uint8Array;
    if (method === 0) data = body.slice();
    else if (method === 8) {
      const stream = new Blob([body as BlobPart]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
      data = new Uint8Array(await new Response(stream).arrayBuffer());
    } else throw new Error(`readZip: unsupported method ${method} for "${name}"`);
    if (crc32(data) !== crc) throw new Error(`readZip: CRC mismatch for "${name}"`);
    out.push({ name, data });
    at += 46 + nameLen + extraLen + commentLen;
  }
  return out;
}
